/* eslint-disable react/style-prop-object */
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash-es';

import { shipmentConversions } from '../app/data/shipmentConversions';
import { setShipmentDetailSummaryView } from '../app/redux/shipmentDetail';
import Whitespace from '../components/Whitespace';
import ProcessingModal from '../components/dialogs/ProcessingModal';
import { useDynamicFormSchemaDynamicFieldsDefinition } from '../components/forms/dynamic/dynamicFormSchema';
import { SimpleCard, SimpleCardTheme } from '../components/layout/cardElements';
import { useLayoutElementsStyles } from '../components/layout/layoutElements.styles';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import { cssVariables } from '../styles/cssVariables';
import ModalPage from '../templates/ModalPage';
import { pxToNumber } from '../utils/cssUtils';
import { DateTimeFormatMedium } from '../utils/dateFormatting';
import ShipmentDetailMap from './shipmentDetail/ShipmentDetailMap';
import ShipmentMilestonesSteps from './shipmentDetail/ShipmentMilestonesSteps';
import { formatDateTimeSchedule } from './shipmentDetail/shipmentFormats';
import { ShipmentDetailResponsiveContent } from './shipmentDetail/shipmentSummaries/ShipmentDetailResponsiveContent';
import { CurrencyFormat } from '../components/domainSpecific/currencyElements';
import { useConfirmOrderPageStyles } from './ConfirmOrderPage.styles';
import { useShipmentDetailSharedStyles } from './shipmentDetail/shipmentDetailShared.styles';

const DateTimeFormat = props => (
  <DateTimeFormatMedium
    {...props}
    separator={
      <>
        <Whitespace />
        <FormattedMessage id="time.prefixBy" />
        <Whitespace />
      </>
    }
  />
);

const Card = props => (
  <SimpleCard {...props} theme={SimpleCardTheme.ROUND_BORDERED} />
);

const VIEWS = ['order-summary', 'package-list'];

function LargeView({ data, shipment, children }) {
  const isQuoteDefined = !isNil(data.quoteTotal);
  const { styles } = useConfirmOrderPageStyles();
  const { styles: sharedStyles } = useShipmentDetailSharedStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();

  return (
    <>
      <Row gutter={pxToNumber(cssVariables.spaceNorm2)} align="stretch">
        <Col className={styles.fullWidth}>
          <Card
            bodyProps={{ className: styles.combined }}
            dividerClassName={styles.fullWidthDivider}
          >
            <Row gutter={pxToNumber(cssVariables.spaceNorm2)} align="stretch">
              <Col className={sharedStyles.leftColumn}>
                <div className={styles.quote}>
                  {isQuoteDefined ? (
                    <>
                      <div className={classNames(styles.quoteLabel, 'size-sm')}>
                        <FormattedMessage id="book.confirmOrder.quoteEstimate" />
                      </div>
                      <div className={classNames(styles.quoteValue, 'size-lg')}>
                        <CurrencyFormat value={data.quoteTotal} />
                      </div>
                      <div className={classNames(styles.quoteLabel, 'size-sm')}>
                        <FormattedMessage id="book.confirmOrder.pickUpReadyTime" />
                      </div>
                      <div
                        className={classNames(styles.quoteValue, 'size-sm')}
                        data-info-id="pickupDateTime"
                        data-format="long"
                      >
                        {formatDateTimeSchedule(
                          shipment.pickupDateTime,
                          DateTimeFormat
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={classNames(styles.quoteLabel, 'size-lg')}>
                        <FormattedMessage id="book.confirmOrder.pickUpReadyTime" />
                      </div>
                      <div
                        className={classNames(styles.quoteValue, 'size-md')}
                        data-info-id="pickupDateTime"
                        data-format="long"
                      >
                        {formatDateTimeSchedule(
                          shipment.pickupDateTime,
                          DateTimeFormat
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Col>
              <Col
                className={classNames(layoutStyles.flexCol, layoutStyles.flex1)}
              >
                <div>
                  <ShipmentMilestonesSteps shipment={shipment} data={data} />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Row
        className={classNames(layoutStyles.flex1, 'flex-nowrap', styles.main)}
        gutter={pxToNumber(cssVariables.spaceNorm2)}
      >
        {children}
        <Col
          className={classNames(layoutStyles.flexCol, 'spaces-vert-norm2')}
          flex="1"
        >
          <ShipmentDetailMap shipment={shipment} />
        </Col>
      </Row>
    </>
  );
}

export default function ConfirmOrderPage({
  data,
  onConfirm,
  onCancel,
  headerComponent: Header,
  headerProps,
  action,
}) {
  const shipperSpecificInfoFields = useDynamicFormSchemaDynamicFieldsDefinition(
    { schemaName: 'serviceInformation.shipperSpecificInfo' }
  );

  const shipmentForm = data;
  const shipment =
    shipmentForm &&
    shipmentConversions.formToDetail(data, { shipperSpecificInfoFields });

  const shipmentWithLocations = {
    ...shipment,
    origin: {
      ...shipment.origin,
      location: data.originLocation,
    },
    destination: {
      ...shipment.destination,
      location: data.destinationLocation,
    },
    serviceAdvisory: data.serviceAdvisory,
  };

  const actionWithArgs = {
    ...action,
    execute: (...args) => action.execute({ shipment, shipmentForm }, ...args),
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setShipmentDetailSummaryView(VIEWS[0]));
  }, [dispatch]);

  const media = useResponsiveQueries();
  const isSm = media.sm || media.xs;
  const { styles } = useConfirmOrderPageStyles();

  return (
    <ModalPage
      id="ConfirmOrderPage"
      innerContentClassName={styles.innerContent}
      header={
        <Header
          shipment={shipmentWithLocations}
          shipmentForm={shipmentForm}
          action={actionWithArgs}
          onConfirm={onConfirm}
          onCancel={onCancel}
          {...headerProps}
        />
      }
      wrapInScrollbar={isSm}
    >
      <ShipmentDetailResponsiveContent
        data={data}
        shipment={shipmentWithLocations}
        summariesViews={VIEWS}
        largeViewComponent={LargeView}
      />
      {shipment.containsTbd && action.loading && (
        <ProcessingModal textId="book.confirmOrder.savingShipment" />
      )}
    </ModalPage>
  );
}
